import { Form, FormikProvider, useFormik } from "formik";
import React, { useRef } from "react";
import { z } from "zod";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import classNames from "classnames";
import commonStyles from "@components/onboarding/steps/common.module.scss";
import { StateAwareLink } from "@components/routes/StateAwareLink";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HorizontalLine } from "@design-system/HorizontalLine/HorizontalLine";
import { TermsAndPrivacyConsent } from "@design-system/TermsAndPrivacyConsent/TermsAndPrivacyConsent";
import {
    GoogleLoginHandle,
    LoginWithGoogleButton,
} from "../GoogleAuth/LoginWithGoogleButton";
import styles from "../Login.module.scss";

const loginFormSchema = z.object({
    phoneNumberOrEmail: z
        .string()
        .min(1)
        .refine(
            (value) =>
                z.string().email().safeParse(value.trim()).success ||
                /^\d{10}$/.test(normalizeValue(value)),
            "Please enter a valid email or 10-digit phone number",
        ),
});

type LoginFormValues = z.infer<typeof loginFormSchema>;

interface LoginFlowStartProps {
    onRequestCode(phoneNumberOrEmail: string): Promise<any>;
    onSuccess(phoneNumberOrEmail: string): void;
}

export const LoginFlowStart: React.FC<LoginFlowStartProps> = ({
    onRequestCode,
    onSuccess,
}) => {
    const googleRef = useRef<GoogleLoginHandle | null>(null);

    useKeyboardCommand({
        keys: "g",
        callback: () => {
            googleRef.current?.login();
        },
        options: {
            description: "Continue with Google",
        },
    });

    const formik = useFormik<LoginFormValues>({
        initialValues: {
            phoneNumberOrEmail: "",
        },
        validate: validateFormikWithZodSchema(loginFormSchema),
        onSubmit: submitHelper({
            handler: async ({ phoneNumberOrEmail }) => {
                const normalized = normalizeValue(phoneNumberOrEmail);
                const requestSuccessful = await onRequestCode(normalized);
                if (requestSuccessful) {
                    onSuccess(normalized);
                }
            },
        }),
    });
    const isPhoneNumberOrEmailInvalid = Boolean(
        formik.values.phoneNumberOrEmail &&
        formik.touched.phoneNumberOrEmail &&
        formik.errors.phoneNumberOrEmail,
    );

    return (
        <>
            <h1 className={classNames(styles.heading, commonStyles.title)}>
                Log in to Kick
            </h1>
            <section className="login__form">
                <LoginWithGoogleButton
                    ref={googleRef}
                    size="xl"
                    variant="primary"
                    block
                    rightIcon={
                        <span data-slot-box="">
                            <Shortcut keys="g" />
                        </span>
                    }
                >
                    Continue with Google
                </LoginWithGoogleButton>

                <HorizontalLine className={styles.spacer}>or</HorizontalLine>

                <FormikProvider value={formik}>
                    <Form
                        className={classNames(styles.form, {
                            [styles.touched]: formik.touched.phoneNumberOrEmail,
                        })}
                    >
                        <Field.Root
                            invalid={isPhoneNumberOrEmailInvalid}
                            className={styles.phoneNumberOrEmailInput}
                        >
                            <Input
                                {...formik.getFieldProps("phoneNumberOrEmail")}
                                size="xl"
                                placeholder="Phone number or email"
                                aria-label="Phone number or email"
                                invalid={isPhoneNumberOrEmailInvalid}
                                onFocus={() =>
                                    formik.setFieldTouched(
                                        "phoneNumberOrEmail",
                                        true,
                                    )
                                }
                            />
                        </Field.Root>
                        <ButtonWithLoader
                            loading={formik.isSubmitting}
                            type="submit"
                            size="xl"
                            className={styles.submitButton}
                            disabled={!formik.isValid || !formik.dirty}
                            variant="primary"
                            hotKey="enter"
                            hotKeyOptions={{
                                description: "Continue",
                            }}
                            hotKeyDisplayPosition="inline"
                        >
                            Log in
                        </ButtonWithLoader>
                    </Form>
                </FormikProvider>
            </section>

            <TermsAndPrivacyConsent />

            <footer className="mt-6 text-center">
                Don't have an account?{" "}
                <StateAwareLink to="/register">Sign up</StateAwareLink>
            </footer>
        </>
    );
};

function normalizeValue(value: string) {
    return value.includes("@")
        ? value.trim()
        : value.trim().replace(/\D/g, "").slice(-10);
}
